import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, Circle, Trophy, TrendingUp, AlertTriangle, ArrowLeft, Paperclip, FileText, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function ProgressShare() {
  const { token } = useParams();
  const [row, setRow] = useState(null);
  const [status, setStatus] = useState("loading");
  const [uploadingKey, setUploadingKey] = useState(null);
  const fileRefs = useRef({});

  useEffect(() => {
    api.get(`/progress/share/${token}`)
      .then(({ data }) => { setRow(data); setStatus("ok"); })
      .catch((err) => setStatus(err?.response?.status === 404 ? "notfound" : "error"));
  }, [token]);

  const uploadAttachment = async (taskIdx, file) => {
    if (!file || !row) return;
    const key = `${taskIdx}`;
    setUploadingKey(key);
    const form = new FormData();
    form.append("file", file);
    form.append("token", token);
    try {
      const { data } = await api.post(`/progress/${row.client_id}/task/${taskIdx}/attach`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setRow((r) => {
        const tasks = [...(r.tasks || [])];
        tasks[taskIdx] = { ...tasks[taskIdx], attachments: [...(tasks[taskIdx].attachments || []), data] };
        return { ...r, tasks };
      });
      toast.success(`${file.name} attached.`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Upload failed.";
      toast.error(msg);
    } finally {
      setUploadingKey(null);
    }
  };

  const downloadAttachment = async (taskIdx, att) => {
    try {
      const res = await api.get(
        `/progress/${row.client_id}/task/${taskIdx}/attachment/${att.id}/download?token=${encodeURIComponent(token)}`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: att.mime || "application/octet-stream" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = att.filename || "file";
      link.rel = "noopener";
      document.body.appendChild(link);
      link.click();
      setTimeout(() => { link.remove(); window.URL.revokeObjectURL(url); }, 800);
    } catch {
      toast.error("Download failed.");
    }
  };

  const removeAttachment = async (taskIdx, att) => {
    if (!window.confirm(`Remove ${att.filename}?`)) return;
    try {
      await api.delete(`/progress/${row.client_id}/task/${taskIdx}/attachment/${att.id}?token=${encodeURIComponent(token)}`);
      setRow((r) => {
        const tasks = [...(r.tasks || [])];
        tasks[taskIdx] = { ...tasks[taskIdx], attachments: (tasks[taskIdx].attachments || []).filter((x) => x.id !== att.id) };
        return { ...r, tasks };
      });
      toast.success("Removed.");
    } catch {
      toast.error("Could not remove.");
    }
  };

  return (
    <div className="min-h-screen bg-[#eae7de] text-[#1e3d3a]">
      <header className="max-w-3xl mx-auto px-6 py-8 flex items-center justify-between">
        <Link to="/" data-testid="share-home" className="flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-[#1e3d3a]/60 hover:text-[#1e3d3a] transition-colors duration-300">
          <ArrowLeft className="h-3.5 w-3.5" /> Elevare Solutions
        </Link>
        <span className="text-[10px] uppercase tracking-[0.28em] text-[#c9a961]">Your progress</span>
      </header>

      <main className="max-w-3xl mx-auto px-6 pb-24">
        {status === "loading" && (
          <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-10 flex items-center justify-center gap-3 text-sm text-[#1e3d3a]/55">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
        {status === "notfound" && (
          <div data-testid="share-notfound" className="rounded-2xl border border-[#b23a48]/25 bg-white p-10 text-center">
            <AlertTriangle className="h-8 w-8 text-[#b23a48] mx-auto" strokeWidth={1.4} />
            <h1 className="mt-4 font-head text-2xl">This link is no longer active</h1>
            <p className="mt-3 text-sm text-[#1e3d3a]/65 max-w-md mx-auto">Reach out to Kalib or John and they&rsquo;ll send a fresh link.</p>
          </div>
        )}
        {status === "ok" && row && (
          <div data-testid="share-view" className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-8 space-y-6">
            <div>
              <span className="text-[10px] uppercase tracking-[0.28em] text-[#c9a961]">{row.business_name || "Your business"}</span>
              <h1 className="mt-3 font-head text-3xl">Recovery progress</h1>
              {row.updated_at && (
                <p className="mt-1 text-xs text-[#1e3d3a]/45">Last update {new Date(row.updated_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}</p>
              )}
            </div>

            {/* BBB rail */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] uppercase tracking-[0.24em] text-[#c9a961] flex items-center gap-1.5">
                  <TrendingUp className="h-3 w-3" /> BBB rating
                </span>
                <span className="text-xs text-[#1e3d3a]/50 tabular-nums">{row.progress_pct}%</span>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="rounded-xl border border-[#1e3d3a]/12 bg-[#eae7de]/60 px-4 py-3 text-center">
                  <div className="text-[9px] uppercase tracking-[0.24em] text-[#1e3d3a]/55">Current</div>
                  <div className="mt-1 font-head text-3xl font-light">{row.current_bbb || "—"}</div>
                </div>
                <div className="rounded-xl border border-[#c9a961]/40 bg-[#c9a961]/8 px-4 py-3 text-center">
                  <div className="text-[9px] uppercase tracking-[0.24em] text-[#a3823f]">Target</div>
                  <div className="mt-1 font-head text-3xl font-light">{row.target_bbb || "—"}</div>
                </div>
              </div>
              <div className="relative h-2.5 rounded-full bg-[#eae7de] overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${row.progress_pct}%` }}
                  transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
                  className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#c9a961] to-[#1e3d3a] rounded-full"
                />
                {row.progress_pct >= 100 && (
                  <Trophy className="absolute right-1 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#c9a961]" />
                )}
              </div>
            </div>

            {/* History */}
            {row.history?.length > 0 && (
              <div>
                <span className="text-[10px] uppercase tracking-[0.24em] text-[#c9a961]">Rating history</span>
                <div className="mt-2 flex flex-wrap items-center gap-1.5 text-sm">
                  {row.history.map((h, i, arr) => (
                    <span key={i} className="flex items-center gap-1.5">
                      <span className="font-head text-lg text-[#1e3d3a]">{h.rating}</span>
                      <span className="text-[10px] text-[#1e3d3a]/45">{new Date(h.at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                      {i < arr.length - 1 && <span className="text-[#c9a961]">→</span>}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Tasks with attachments */}
            {row.tasks?.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] uppercase tracking-[0.24em] text-[#c9a961]">Milestones</span>
                  <span className="text-xs text-[#1e3d3a]/50 tabular-nums">{row.completed}/{row.total_tasks}</span>
                </div>
                <p className="mb-3 text-[11px] text-[#1e3d3a]/50">
                  Attach screenshots or receipts so Kalib &amp; John can verify each milestone.
                </p>
                <ul className="space-y-1.5">
                  {row.tasks.map((t, i) => {
                    const upKey = `${i}`;
                    const uploading = uploadingKey === upKey;
                    const atts = t.attachments || [];
                    return (
                      <li key={i} className="rounded-lg hover:bg-[#eae7de]/40 transition-colors duration-300 px-1 py-1">
                        <div className="flex items-center gap-2 group">
                          {t.done
                            ? <CheckCircle2 className="h-4 w-4 text-[#c9a961] shrink-0" strokeWidth={1.8} />
                            : <Circle className="h-4 w-4 text-[#1e3d3a]/30 shrink-0" strokeWidth={1.6} />}
                          <span className={`flex-1 text-sm ${t.done ? "text-[#1e3d3a]/45 line-through" : "text-[#1e3d3a]"}`}>{t.label}</span>
                          <input
                            type="file"
                            ref={(el) => { fileRefs.current[upKey] = el; }}
                            className="hidden"
                            data-testid={`share-attach-input-${i}`}
                            onChange={(e) => { if (e.target.files?.[0]) uploadAttachment(i, e.target.files[0]); e.target.value = ""; }}
                          />
                          <button
                            type="button"
                            data-testid={`share-attach-${i}`}
                            onClick={() => fileRefs.current[upKey]?.click()}
                            disabled={uploading}
                            className="text-[#1e3d3a]/45 hover:text-[#a3823f] transition-colors duration-300 disabled:opacity-40"
                            title="Attach file"
                          >
                            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" strokeWidth={1.6} />}
                          </button>
                        </div>
                        {atts.length > 0 && (
                          <ul className="mt-1.5 ml-6 space-y-1">
                            {atts.map((att) => (
                              <li key={att.id} className="flex items-center gap-2 text-[11px] text-[#1e3d3a]/70">
                                <button
                                  type="button"
                                  data-testid={`share-attach-download-${att.id}`}
                                  onClick={() => downloadAttachment(i, att)}
                                  className="inline-flex items-center gap-1 hover:text-[#a3823f] transition-colors duration-300 max-w-[240px]"
                                >
                                  <FileText className="h-3 w-3 shrink-0" />
                                  <span className="truncate">{att.filename}</span>
                                </button>
                                <button
                                  type="button"
                                  data-testid={`share-attach-remove-${att.id}`}
                                  onClick={() => removeAttachment(i, att)}
                                  className="text-[#1e3d3a]/35 hover:text-[#b23a48] transition-colors duration-300"
                                  title="Remove"
                                >
                                  <Trash2 className="h-3 w-3" strokeWidth={1.6} />
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
